import React, { useCallback, useEffect, useState } from "react";
import { X, Save, UserPlus, Search } from "lucide-react";
import GlassPanel from "@/components/GlassPanel";
import api from "@/lib/api";
import { toast } from "sonner";
import { formatApiError } from "@/context/AuthContext";

// Same 10 tests + units as the athlete-side scoring form.
const TESTS = [
  { key: "deadlift_kg",         label: "DEADLIFT (1RM)",       unit: "kg" },
  { key: "bench_kg",            label: "BENCH PRESS (1RM)",    unit: "kg" },
  { key: "forty_yard_sec",      label: "40-YARD DASH",         unit: "s"  },
  { key: "shuttle_5_10_5_sec",  label: "5-10-5 SHUTTLE",       unit: "s"  },
  { key: "vertical_jump_cm",    label: "VERTICAL JUMP",        unit: "cm" },
  { key: "broad_jump_cm",       label: "BROAD JUMP",           unit: "cm" },
  { key: "five_min_shuttle_m",  label: "5-MIN SHUTTLE",        unit: "m"  },
  { key: "mile_sec",            label: "MILE RUN",             unit: "s"  },
  { key: "dead_hang_sec",       label: "DEAD HANG",            unit: "s"  },
  { key: "single_leg_stand_sec",label: "SINGLE-LEG STAND",     unit: "s"  },
];

const emptyForm = () => ({
  height_cm: "", weight_kg: "",
  ...Object.fromEntries(TESTS.map((t) => [t.key, ""])),
});

/**
 * Two-in-one dialog:
 *   mode="create"  → admin picks an athlete, enters H/W + 10 tests, POST /admin/scores.
 *   mode="edit"    → admin edits H/W + any of the 10 tests on an existing score.
 * Both flows trigger a GPS + archetype recompute server-side.
 */
export default function AdminScoreDialog({ mode = "create", initialScore = null, onClose, onSaved }) {
  const isEdit = mode === "edit" && initialScore;

  // Athlete pick (create mode only).
  const [athleteQuery, setAthleteQuery] = useState("");
  const [athleteHits, setAthleteHits] = useState([]);
  const [pickedAthlete, setPickedAthlete] = useState(null);
  const [searching, setSearching] = useState(false);

  const [form, setForm] = useState(emptyForm());
  const [busy, setBusy] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Debounced athlete search (create mode only).
  useEffect(() => {
    if (isEdit) return;
    const q = athleteQuery.trim();
    if (q.length < 2) { setAthleteHits([]); return; }
    let cancelled = false;
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await api.get(`/admin/search`, { params: { q } });
        if (!cancelled) setAthleteHits((data.users || []).filter((u) => u.role !== "admin"));
      } catch {
        if (!cancelled) setAthleteHits([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(t); };
  }, [athleteQuery, isEdit]);

  // Preload H/W + raw when editing.
  useEffect(() => {
    if (!isEdit) return;
    const s = initialScore;
    setForm({
      height_cm: s.height_cm ?? s.snapshot?.height_cm ?? "",
      weight_kg: s.weight_kg ?? s.snapshot?.weight_kg ?? "",
      ...Object.fromEntries(TESTS.map((t) => [t.key, s.raw?.[t.key] ?? ""])),
    });
  }, [isEdit, initialScore]);

  const pickAthlete = (u) => {
    setPickedAthlete(u);
    // Prefill H/W from the athlete's profile if available.
    setForm((f) => ({
      ...f,
      height_cm: u.height_cm ?? "",
      weight_kg: u.weight_kg ?? "",
    }));
    setAthleteQuery("");
    setAthleteHits([]);
  };

  const submit = async () => {
    const h = parseFloat(form.height_cm);
    const w = parseFloat(form.weight_kg);
    if (Number.isNaN(h) || Number.isNaN(w)) {
      toast.error("Height and Weight are required");
      return;
    }
    if (h < 80 || h > 250) { toast.error("Height must be 80-250 cm"); return; }
    if (w < 15 || w > 250) { toast.error("Weight must be 15-250 kg"); return; }
    setBusy(true);
    try {
      if (isEdit) {
        const payload = { height_cm: h, weight_kg: w };
        for (const t of TESTS) {
          const v = form[t.key];
          if (v === "" || v == null) continue;
          const parsed = parseFloat(v);
          if (Number.isNaN(parsed)) {
            toast.error(`${t.label}: invalid number`);
            setBusy(false);
            return;
          }
          payload[t.key] = parsed;
        }
        await api.patch(`/admin/scores/${initialScore.id}`, payload);
        toast.success("Score updated · GPS recomputed");
      } else {
        if (!pickedAthlete) { toast.error("Pick an athlete first"); setBusy(false); return; }
        const payload = { user_id: pickedAthlete.id, height_cm: h, weight_kg: w };
        let hasAny = false;
        for (const t of TESTS) {
          const v = form[t.key];
          if (v === "" || v == null) continue;
          const parsed = parseFloat(v);
          if (Number.isNaN(parsed)) {
            toast.error(`${t.label}: invalid number`);
            setBusy(false);
            return;
          }
          payload[t.key] = parsed;
          hasAny = true;
        }
        if (!hasAny) { toast.error("Enter at least one test value"); setBusy(false); return; }
        await api.post(`/admin/scores`, payload);
        toast.success(`${pickedAthlete.name}: score saved`);
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const heading = isEdit ? "EDIT SCORE" : "ADD SCORE FOR ATHLETE";
  const subheading = isEdit
    ? `${initialScore.snapshot?.name || initialScore.owner?.name || "—"} · ${initialScore.snapshot?.gender || "—"} · ${new Date(initialScore.created_at).toLocaleString()}`
    : pickedAthlete
      ? `${pickedAthlete.name} · ${pickedAthlete.gender || "—"} · ${pickedAthlete.email}`
      : "SEARCH THE GPS DATABASE FOR AN ATHLETE";

  return (
    <div
      className="fixed inset-0 z-[95] grid place-items-center p-4 bg-black/85 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
      data-testid="admin-score-dialog"
    >
      <GlassPanel
        className="p-6 w-full max-w-3xl max-h-[92vh] overflow-y-auto relative"
        scanline
        onClick={(e) => e.stopPropagation()}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-mute hover:text-white"
          aria-label="Close"
          data-testid="admin-score-dialog-close"
        >
          <X size={18} />
        </button>

        <div className="mb-4">
          <div className="gps-label">{heading}</div>
          <div className="text-mute gps-mono text-[10px] tracking-[0.2em] mt-1">{subheading}</div>
        </div>

        {/* Athlete picker — create mode only */}
        {!isEdit && (
          <div className="mb-5">
            {pickedAthlete ? (
              <div className="flex items-center justify-between border border-cyan-400/40 bg-cyan-400/[0.06] rounded-sm p-3">
                <div>
                  <div className="text-white text-sm">{pickedAthlete.name}</div>
                  <div className="gps-mono text-[10px] text-cyan-400 tracking-[0.2em] mt-1">
                    {pickedAthlete.public_id || "—"} · {pickedAthlete.gender || "—"} · {pickedAthlete.email}
                  </div>
                </div>
                <button
                  className="gps-btn ghost !px-2"
                  onClick={() => setPickedAthlete(null)}
                  data-testid="admin-score-clear-athlete"
                  title="Choose a different athlete"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <>
                <div className="relative">
                  <Search size={12} className="absolute left-3 top-1/2 -translate-y-1/2 text-mute pointer-events-none" />
                  <input
                    className="gps-input w-full pl-8"
                    placeholder="SEARCH ATHLETE BY NAME, EMAIL OR ID…"
                    value={athleteQuery}
                    onChange={(e) => setAthleteQuery(e.target.value)}
                    autoFocus
                    data-testid="admin-score-search"
                  />
                </div>
                {(athleteHits.length > 0 || searching) && (
                  <div className="mt-2 border border-white/10 rounded-sm max-h-56 overflow-y-auto">
                    {searching && (
                      <div className="p-3 text-mute gps-mono text-[10px] tracking-[0.2em]">SEARCHING…</div>
                    )}
                    {athleteHits.map((u) => (
                      <button
                        key={u.id}
                        onClick={() => pickAthlete(u)}
                        className="w-full text-left p-3 border-b border-white/5 hover:bg-white/[0.04]"
                        data-testid={`admin-score-hit-${u.id}`}
                      >
                        <div className="flex items-center gap-2">
                          <UserPlus size={12} className="text-cyan" />
                          <span className="text-white text-sm">{u.name}</span>
                        </div>
                        <div className="gps-mono text-[10px] tracking-[0.15em] mt-1 flex gap-3">
                          <span className="text-cyan-400">{u.public_id || "—"}</span>
                          <span className="text-mute truncate">{u.email}</span>
                        </div>
                      </button>
                    ))}
                    {!searching && athleteHits.length === 0 && athleteQuery.trim().length >= 2 && (
                      <div className="p-3 text-mute gps-mono text-[10px] tracking-[0.2em]">NO MATCHES</div>
                    )}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {/* H/W + tests grid */}
        {(isEdit || pickedAthlete) && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="gps-label">HEIGHT (CM)</label>
                <input
                  className="gps-input"
                  type="number" min="80" max="250" step="any"
                  value={form.height_cm}
                  onChange={(e) => set("height_cm", e.target.value)}
                  data-testid="admin-score-height"
                />
              </div>
              <div>
                <label className="gps-label">WEIGHT (KG)</label>
                <input
                  className="gps-input"
                  type="number" min="15" max="250" step="any"
                  value={form.weight_kg}
                  onChange={(e) => set("weight_kg", e.target.value)}
                  data-testid="admin-score-weight"
                />
              </div>
              {TESTS.map((t) => (
                <div key={t.key}>
                  <label className="gps-label">
                    {t.label} ({t.unit})
                  </label>
                  <input
                    className="gps-input"
                    type="number" step="any"
                    value={form[t.key]}
                    onChange={(e) => set(t.key, e.target.value)}
                    data-testid={`admin-score-input-${t.key}`}
                  />
                </div>
              ))}
            </div>

            <div className="mt-6 flex gap-2 justify-end">
              <button
                onClick={onClose}
                className="gps-btn ghost"
                disabled={busy}
                data-testid="admin-score-cancel"
              >
                CANCEL
              </button>
              <button
                onClick={submit}
                className="gps-btn"
                disabled={busy}
                data-testid="admin-score-submit"
              >
                <Save size={14} /> {busy ? "SAVING…" : "SAVE SCORE"}
              </button>
            </div>
          </>
        )}
      </GlassPanel>
    </div>
  );
}
